import { api } from '../utils/jsonp.js'
import { aesEncrypt } from '../utils/aes.js'

/**
 * 验证码模块
 */
export class Captcha {
  constructor(options = {}) {
    this.imgWidth = options.imgWidth || 310
    this.imgHeight = options.imgHeight || 155
    this.barHeight = options.barHeight || 40
    this.vSpace = options.vSpace || 5
    this.onSuccess = options.onSuccess || (() => {})
    this.onError = options.onError || (() => {})

    this.isVisible = false
    this.isLoading = false
    this.token = ''
    this.secretKey = ''
    this.originalImage = ''
    this.jigsawImage = ''
    this.sliderPosition = 0
    this.isDragging = false
    this.startX = 0
    this.startLeft = 0
    this.status = ''
    this.message = ''
    this.phone = ''
    this.element = null
  }

  getClientUid() {
    let sliderUid = localStorage.getItem('slider')
    if (!sliderUid) {
      const s = []
      const hexDigits = '0123456789abcdef'
      for (let i = 0; i < 36; i++) {
        s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1)
      }
      s[14] = '4'
      s[19] = hexDigits.substr((parseInt(s[19], 16) & 0x3) | 0x8, 1)
      s[8] = s[13] = s[18] = s[23] = '-'
      sliderUid = 'slider-' + s.join('')
      localStorage.setItem('slider', sliderUid)
    }
    return sliderUid
  }

  async refresh() {
    this.isLoading = true
    this.status = ''
    this.message = ''
    this.sliderPosition = 0

    try {
      const result = await api.getCaptcha(this.getClientUid())

      if (result.repCode === '0000') {
        this.token = result.repData.token
        this.secretKey = result.repData.secretKey
        this.originalImage = 'data:image/png;base64,' + result.repData.originalImageBase64
        this.jigsawImage = 'data:image/png;base64,' + result.repData.jigsawImageBase64
        this.updateUI()
      } else {
        this.status = 'error'
        this.message = result.repMsg || '获取验证码失败'
        this.onError(this.message)
      }
    } catch (error) {
      console.error('刷新验证码失败:', error)
      this.status = 'error'
      this.message = '网络错误'
      this.onError(this.message)
    } finally {
      this.isLoading = false
    }
  }

  async show(phoneNumber) {
    if (!phoneNumber) {
      return { success: false, message: '请输入手机号码' }
    }

    const phoneReg = /^1[345789]\d{9}$/
    if (!phoneReg.test(phoneNumber)) {
      return { success: false, message: '手机号格式不正确' }
    }

    this.phone = phoneNumber
    this.isVisible = true
    this.element.style.display = 'flex'
    await this.refresh()
    return { success: true }
  }

  hide() {
    this.isVisible = false
    this.status = ''
    this.message = ''
    this.sliderPosition = 0
    if (this.element) {
      this.element.style.display = 'none'
    }
  }

  startDrag(e) {
    if (this.status === 'success') return

    this.isDragging = true
    this.startX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
    this.startLeft = this.sliderPosition
  }

  onDrag(e) {
    if (!this.isDragging || this.status === 'success') return

    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
    const diff = clientX - this.startX
    const maxLeft = this.imgWidth - this.barHeight
    this.sliderPosition = Math.max(0, Math.min(this.startLeft + diff, maxLeft))
    this.updateSliderUI()
  }

  async endDrag() {
    if (!this.isDragging || this.status === 'success') return

    this.isDragging = false

    const moveDistance = Math.floor(this.sliderPosition * 310 / this.imgWidth)

    const pointJson = this.secretKey
      ? aesEncrypt(JSON.stringify({ x: moveDistance, y: 5.0 }), this.secretKey)
      : JSON.stringify({ x: moveDistance, y: 5.0 })

    try {
      const data = {
        captchaType: 'blockPuzzle',
        pointJson,
        token: this.token,
        clientUid: this.getClientUid(),
        ts: Date.now(),
        phone: this.phone
      }

      const result = await api.checkCaptcha(data)

      if (result.repCode === '0000') {
        this.status = 'success'
        this.message = '验证成功'
        this.updateStatusUI()

        const smsResult = await api.sendMessage(result.phone, result.gamerskyToken, result.gsTs)

        if (smsResult.repCode === 1) {
          this.onSuccess()
          setTimeout(() => this.hide(), 1000)
          return { success: true }
        } else {
          this.onError(smsResult.repMsg || '发送短信失败')
          return { success: false, message: smsResult.repMsg || '发送短信失败' }
        }
      } else {
        this.status = 'error'
        this.message = '验证失败'
        this.updateStatusUI()
        setTimeout(() => this.refresh(), 1000)
        return { success: false }
      }
    } catch (error) {
      console.error('验证失败:', error)
      this.status = 'error'
      this.message = '验证失败'
      this.updateStatusUI()
      return { success: false }
    }
  }

  updateUI() {
    if (!this.element) return

    const backImg = this.element.querySelector('.backImg')
    const bockBackImg = this.element.querySelector('.bock-backImg')

    if (backImg) backImg.src = this.originalImage
    if (bockBackImg) bockBackImg.src = this.jigsawImage

    this.updateSliderUI()
  }

  updateSliderUI() {
    if (!this.element) return

    const moveBlock = this.element.querySelector('.verify-move-block')
    const leftBar = this.element.querySelector('.verify-left-bar')

    if (moveBlock) {
      moveBlock.style.left = this.sliderPosition + 'px'
    }
    if (leftBar) {
      leftBar.style.width = (this.barHeight + this.sliderPosition) + 'px'
    }
  }

  updateStatusUI() {
    if (!this.element) return

    const tips = this.element.querySelector('.verify-tips')
    const moveBlock = this.element.querySelector('.verify-move-block')
    const leftBar = this.element.querySelector('.verify-left-bar')
    const icon = this.element.querySelector('.verify-icon')

    if (tips) {
      tips.textContent = this.message
      tips.style.bottom = '0px'
      tips.className = 'verify-tips ' + (this.status === 'success' ? 'suc-bg' : 'err-bg')
    }

    if (moveBlock) {
      moveBlock.style.backgroundColor = this.status === 'success' ? '#5cb85c' : this.status === 'error' ? '#d9534f' : '#fff'
    }

    if (leftBar) {
      leftBar.style.borderColor = this.status === 'success' ? '#5cb85c' : this.status === 'error' ? '#d9534f' : '#ddd'
    }

    if (icon) {
      icon.className = 'verify-icon iconfont ' + (this.status === 'success' ? 'icon-check' : this.status === 'error' ? 'icon-close' : 'icon-right')
    }
  }

  /**
   * 渲染验证码弹窗 - 严格按照 Captcha.vue 模板
   */
  render() {
    const container = document.createElement('div')
    container.className = 'loginmask'
    container.style.display = 'none'
    container.innerHTML = `
      <div class="verifybox" style="width: ${this.imgWidth + 30}px">
        <div class="verifybox-top">
          请完成安全验证
          <span class="verifybox-close">×</span>
        </div>
        <div class="verifybox-bottom">
          <div style="position: relative;">
            <div class="verify-img-out" style="height: ${this.imgHeight + this.vSpace}px">
              <div class="verify-img-panel" style="width: ${this.imgWidth}px; height: ${this.imgHeight}px">
                <div class="verify-refresh" style="z-index: 3">
                  <i class="iconfont icon-refresh"></i>
                </div>
                <span class="verify-tips"></span>
                <img class="backImg" style="width: 100%; height: 100%; display: block">
              </div>
            </div>
            <div class="verify-bar-area" style="width: ${this.imgWidth}px; height: ${this.barHeight}px; line-height: ${this.barHeight}px">
              <span class="verify-msg">向右滑动完成验证</span>
              <div class="verify-left-bar" style="width: ${this.barHeight}px; height: ${this.barHeight}px">
                <span class="verify-msg"></span>
                <div class="verify-move-block" style="width: ${this.barHeight}px; height: ${this.barHeight}px; left: 0; background-color: #fff">
                  <i class="verify-icon iconfont icon-right" style="color: #fff"></i>
                  <div class="verify-sub-block" style="left: 0; top: -${this.imgHeight + this.vSpace}px; width: ${Math.floor(this.imgWidth * 47 / 310)}px; height: ${this.imgHeight}px">
                    <img class="bock-backImg" style="width: 100%; height: 100%; display: block">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    this.element = container
    this.bindEvents()

    return container
  }

  bindEvents() {
    if (!this.element) return

    const self = this
    const closeBtn = this.element.querySelector('.verifybox-close')
    const refreshBtn = this.element.querySelector('.verify-refresh')
    const moveBlock = this.element.querySelector('.verify-move-block')

    closeBtn.addEventListener('click', () => self.hide())

    this.element.addEventListener('click', (e) => {
      if (e.target === self.element) {
        self.hide()
      }
    })

    refreshBtn.addEventListener('click', () => self.refresh())

    moveBlock.addEventListener('mousedown', (e) => self.startDrag(e))
    moveBlock.addEventListener('touchstart', (e) => self.startDrag(e))

    document.addEventListener('mousemove', (e) => self.onDrag(e))
    document.addEventListener('touchmove', (e) => self.onDrag(e))
    document.addEventListener('mouseup', () => self.endDrag())
    document.addEventListener('touchend', () => self.endDrag())
  }
}